import { Check } from 'lucide-react';
import { Skeleton } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { haptics } from '@/shared/lib/telegram/haptics';
import { practiceCoverFor } from '../model/cover';
import { practiceIconFor } from '../model/icons';
import { PracticeCover } from './PracticeCover';
import type { PracticePaletteEntry } from '../model/palette';
import type { PracticeScenario } from '../model/types';

/**
 * Карточка практики (ролевая игра или идиома — общая разметка, разница
 * только в тексте/иконке через `scenario`) — общая для превью на главном
 * (`widgets/topics`) и сеток на экранах «Ролевые игры»/«Идиомы»
 * (`widgets/topics-list`). Разметка — плитка-«обложка» (квадрат, радиальный
 * градиент, бейдж статуса поверх) плюс подпись под ней, один в один с
 * образцом каталога статей, который дал Дмитрий. У ролевых игр в квадрате
 * обложка с маскотом (`practiceCoverFor`), у идиом обложек пока нет — там
 * крупная иконка на градиенте.
 */
export function PracticeCard({
  scenario,
  palette,
  onOpen,
}: {
  scenario: PracticeScenario;
  palette: PracticePaletteEntry;
  onOpen: () => void;
}) {
  const { t } = useTranslation('PracticeCard');
  const Icon = practiceIconFor(scenario.icon, scenario.kind);
  const cover = practiceCoverFor(scenario.icon, scenario.kind);
  const gradient = `radial-gradient(120% 100% at 50% 30%, #ffffff 0%, ${palette.from} 55%, ${palette.to} 100%)`;
  const iconTile = (
    <div className="flex h-full w-full items-center justify-center" style={{ background: gradient }}>
      <Icon className="size-12 text-ink/50" aria-hidden="true" />
    </div>
  );

  return (
    <button
      type="button"
      onClick={() => {
        haptics.advance();
        onOpen();
      }}
      className="flex w-full flex-col overflow-hidden rounded-2xl border border-hairline bg-glass text-left shadow-[0_6px_16px_rgba(0,0,0,0.12)] transition-transform active:scale-[0.97]"
    >
      <div className="relative aspect-square w-full overflow-hidden">
        {scenario.status === 'new' && (
          <span className="absolute top-2 left-2 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
            {t('newBadge')}
          </span>
        )}
        {scenario.status === 'completed' && (
          <span className="absolute top-2 left-2 z-10 flex items-center gap-0.5 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
            <Check className="size-3" strokeWidth={3} aria-hidden="true" />
            {t('completedBadge')}
          </span>
        )}

        {/* Градиент — только у карточки без обложки (идиомы); обложка, пока грузится, — шиммер. */}
        {cover ? (
          <PracticeCover src={cover} lazy fallback={iconTile} />
        ) : (
          iconTile
        )}
      </div>

      <div className="min-w-0 p-3">
        <p className="line-clamp-2 text-[14px] font-medium text-ink">{scenario.title}</p>
      </div>
    </button>
  );
}

export function PracticeCardSkeleton() {
  return (
    <div className="flex w-full flex-col overflow-hidden rounded-2xl border border-hairline bg-glass shadow-[0_6px_16px_rgba(0,0,0,0.12)]">
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="p-3">
        <Skeleton className="h-4 w-full" />
      </div>
    </div>
  );
}
